'use client';

import { Link, useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Briefcase, Users } from 'lucide-react';
import { formatSalaryGBP } from '@/lib/formatSalaryGBP.js';
import ShareMenu from '@/components/ShareMenu.jsx';
import { createJobCardSummary, getListingDisplayDate } from '@/lib/jobPresentation.js';
import { isFeaturedJob, isPromotionActive } from '@/lib/jobOrdering.js';
import { trackSiteEvent } from '@/lib/siteMetrics.js';
import { getEffectiveSalary } from '@/lib/jobSalary.js';
import { getEffectiveCareerStage, shouldFeatureCareerStageBadge } from '@/lib/jobCareerStage.js';

/**
 * JobCard Component
 * Displays a job listing with luxury brand detection, recruiter info, location, employment type, salary, and action buttons.
 */
export default function JobCard({ job }) {
  const currentLocation = useLocation();
  if (!job) return null;

  const {
    id,
    title,
    store,
    location,
    employment_type,
    job_type,
    apply_link,
    luxury_brand,
    recruiter,
    department,
    description,
    date_added,
    import_date,
    source_type,
  } = job;

  const formattedDate = date_added ? (() => {
    try {
      const date = getListingDisplayDate(date_added, import_date || job.created, source_type);
      if (!date || isNaN(date.getTime())) return null;
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return null;
    }
  })() : null;

  const formattedSalary = formatSalaryGBP(getEffectiveSalary(job), employment_type || job_type);
  const descriptionSummary = createJobCardSummary(description, title);
  const promoted = isPromotionActive(job);
  const featured = !promoted && isFeaturedJob(job);
  const careerStage = getEffectiveCareerStage(job);

  return (
    <Card className={`hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex flex-col h-full ${promoted || featured ? 'border-amber-200 bg-amber-50/30 shadow-md' : 'shadow-sm'}`}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <CardTitle className="text-featured-title text-foreground">{title}</CardTitle>
            <CardDescription className="text-lg md:text-xl font-medium text-muted-foreground">{store}</CardDescription>
          </div>
          {(promoted || featured) && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 hover:bg-amber-100 whitespace-nowrap text-sm px-3 py-1">
              {promoted ? 'Promoted' : 'Featured'}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5 flex flex-col flex-1 pt-2">
        <div className="flex flex-col gap-3">
          {luxury_brand && luxury_brand !== 'Unknown' && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-medium text-foreground">Brand:</span>
              <Badge variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 text-sm">
                {luxury_brand}
              </Badge>
            </div>
          )}

          {recruiter && (
            <div className="flex items-center gap-2 flex-wrap">
              <Users size={18} className="text-blue-600" />
              <span className="text-base font-medium text-foreground">Recruiter:</span>
              <Badge variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50 text-sm">
                {recruiter}
              </Badge>
            </div>
          )}

          {location && (
            <div className="flex items-center gap-2 flex-wrap">
              <MapPin size={18} className="text-muted-foreground" />
              <span className="text-base md:text-lg text-muted-foreground">{location}</span>
            </div>
          )}

          {department && (
            <div className="flex items-center gap-2 flex-wrap">
              <Briefcase size={18} className="text-muted-foreground" />
              <span className="text-base md:text-lg text-muted-foreground">{department}</span>
            </div>
          )}
        </div>

        {employment_type && (
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <Badge
              variant="secondary"
              className={`text-sm px-3 py-1 ${
                employment_type === 'Full-time'
                  ? 'bg-blue-100 text-blue-800'
                  : employment_type === 'Part-time'
                    ? 'bg-orange-100 text-orange-800'
                    : employment_type === 'Contract'
                      ? 'bg-red-100 text-red-800'
                      : employment_type === 'Internship'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-muted text-muted-foreground'
              } hover:brightness-95`}
            >
              {employment_type}
            </Badge>
          </div>
        )}

        {shouldFeatureCareerStageBadge(job) && (
          <div>
            <Badge variant="outline" className="border-primary/20 bg-primary/5 text-primary hover:bg-primary/5">
              {careerStage}
            </Badge>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap mt-2">
          <span className="text-lg md:text-xl font-semibold text-foreground tracking-tight">
            {formattedSalary}
          </span>
        </div>

        {descriptionSummary && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-base md:text-lg text-muted-foreground line-clamp-2 leading-relaxed">{descriptionSummary}</p>
          </div>
        )}

        <div className="mt-auto pt-6 flex flex-col gap-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground font-medium">
            <div>
              {formattedDate && <span>Listed: {formattedDate}</span>}
            </div>
            {(formattedDate || apply_link) && (
              <ShareMenu applyUrl={window.location.origin + `/jobs/${id}`} title={title} />
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              asChild
              variant="outline"
              size="lg"
              className="w-full sm:flex-1 text-base transition-all duration-200 active:scale-[0.98]"
            >
              <Link
                to={`/jobs/${id}`}
                state={currentLocation.pathname === '/jobs' ? { fromJobs: true } : undefined}
              >
                View Details
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              className="w-full sm:flex-1 text-base bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 active:scale-[0.98]"
            >
              <a href={apply_link || '#'} target="_blank" rel="noopener noreferrer" onClick={() => trackSiteEvent('apply_click', { pagePath: `/jobs/${id}`, jobId: id, sourceType: source_type })}>
                Apply Now
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
